<template>
  <div class="sql-view-aggregation-builder">
    <!-- Group By (hidden for count/KPI: a single number has nothing to group) -->
    <div v-if="requireGroupBy" class="mb-3">
      <span class="sql-view-agg-sublabel">Group by</span>
      <v-autocomplete
        v-model="aggregation.groupBy"
        :items="groupByItems"
        :filter="groupByFilter"
        item-text="text"
        item-value="name"
        placeholder="Search columns to group by"
        dense
        outlined
        hide-details
        multiple
        chips
        small-chips
        deletable-chips
        class="mt-1"
        @change="emitChange">
        <template #item="{ item }">
          <div class="d-flex align-center" style="gap: 6px">
            <v-icon x-small color="grey">{{ typeIcon(item.dataType) }}</v-icon>
            <span>{{ item.displayName || item.name }}</span>
          </div>
        </template>
      </v-autocomplete>
      <!-- Contextual copy for multi-dimension types (e.g. stacked column: slot 2 = "stack by"). -->
      <p v-if="groupByHint" class="sql-view-agg-hint">{{ groupByHint }}</p>
    </div>

    <!-- Metrics (the Add calculation action lives in the parent section header) -->
    <div>
      <span class="sql-view-agg-sublabel">{{ requireGroupBy ? 'Values to calculate' : 'Value to show' }}</span>

      <div v-if="!aggregation.metrics.length" class="text-caption text--disabled py-2">
        {{ requireGroupBy
          ? 'Group by a column first, then add a calculation such as a count per group.'
          : 'Add the number your KPI shows, for example a count of records.' }}
      </div>

      <div
        v-for="(metric, index) in aggregation.metrics"
        :key="index"
        class="sql-view-metric-row d-flex align-center mt-2"
        style="gap: 8px">
        <!-- Function -->
        <v-select
          v-model="metric.function"
          :items="aggregationFunctions"
          placeholder="Function"
          dense
          outlined
          hide-details
          style="flex: 1"
          @change="emitChange">
        </v-select>

        <!-- Column -->
        <v-select
          v-model="metric.column"
          :items="metricColumns(metric.function)"
          item-text="name"
          item-value="name"
          placeholder="Column"
          dense
          outlined
          hide-details
          style="flex: 1.5"
          @change="emitChange">
          <template #item="{ item }">
            <div class="d-flex align-center" style="gap: 6px">
              <v-icon x-small color="grey">{{ typeIcon(item.dataType) }}</v-icon>
              <span>{{ item.name }}</span>
              <span class="text-caption text--disabled ml-1">({{ friendlyTypeLabel(item.dataType) }})</span>
            </div>
          </template>
        </v-select>

        <!-- Alias -->
        <v-text-field
          v-model="metric.alias"
          :placeholder="defaultAlias(metric)"
          dense
          outlined
          hide-details
          style="flex: 1.5"
          @input="emitChange">
        </v-text-field>

        <!-- Remove -->
        <v-btn
          v-if="canChangeMetricCount"
          icon
          x-small
          color="error"
          :aria-label="`Remove calculation ${index + 1}`"
          @click="removeMetric(index)">
          <v-icon x-small>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { NUMERIC_SQL_TYPES, DATE_SQL_TYPES, friendlyType } from '../utils/columnTypes';
import { ANY_TYPE_FUNCTIONS, FUNCTION_LABELS, metricLabel } from '../utils/metricLabel';

// System/key columns de-prioritized under a "Technical columns" subheader in the Group by
// picker (still selectable, never hidden). Guid-typed columns join them at render time.
const TECHNICAL_GROUP_BY_COLUMNS = Object.freeze(['Id', 'RegisterId', 'FacilityId', 'RowIndex']);

export default {
  props: {
    value: {
      type: Object,
      default: () => ({ groupBy: [], metrics: [] }),
    },
    columns: {
      type: Array,
      default: () => [],
    },
    // When true (charts), a calculation needs a group-by column first. Count/KPI widgets
    // pass false so they can add an ungrouped total (a metric with no group-by).
    requireGroupBy: {
      type: Boolean,
      default: true,
    },
    // Optional caption under the Group by select. Multi-dimension types use it to explain the
    // slot ordering (stacked column: first column = category, second = the series to stack by).
    groupByHint: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      aggregation: this.initAggregation(this.value),
      aggregationFunctions: Object.keys(FUNCTION_LABELS).map(value => ({
        text: FUNCTION_LABELS[value],
        value,
      })),
    };
  },

  methods: {
    initAggregation(val) {
      if (!val) return { groupBy: [], metrics: [] };
      const differenceBucketGroup = this.copyDifferenceBucketGroup(val.differenceBucketGroup);
      return {
        groupBy: val.groupBy ? [...val.groupBy] : [],
        metrics: val.metrics ? val.metrics.map(m => ({ ...m })) : [],
        ...(differenceBucketGroup ? { differenceBucketGroup } : {}),
      };
    },

    copyDifferenceBucketGroup(bucketGroup) {
      if (!bucketGroup) return null;
      return {
        ...bucketGroup,
        ...(Array.isArray(bucketGroup.upperBounds) ? { upperBounds: [...bucketGroup.upperBounds] } : {}),
        ...(Array.isArray(bucketGroup.labels) ? { labels: [...bucketGroup.labels] } : {}),
      };
    },

    /** Public: the parent section header's "Add calculation" button calls this via ref.
     *  When requireGroupBy (charts), a calculation needs a group-by column first
     *  (mirrors the button's disabled state); count/KPI widgets allow an ungrouped total. */
    addMetric() {
      if (!this.canChangeMetricCount) return;
      if (this.requireGroupBy && (!this.aggregation.groupBy || !this.aggregation.groupBy.length)) return;
      this.aggregation.metrics.push({
        function: 'COUNT',
        column: this.numericColumns.length ? this.numericColumns[0].name : (this.columns[0]?.name || ''),
        alias: '',
      });
      this.emitChange();
    },

    removeMetric(index) {
      if (!this.canChangeMetricCount) return;
      this.aggregation.metrics.splice(index, 1);
      this.emitChange();
    },

    metricColumns(fn) {
      // COUNT / COUNT_DISTINCT work on any column; SUM/AVG/MIN/MAX need numeric
      if (ANY_TYPE_FUNCTIONS.includes(fn)) return this.columns;
      return this.numericColumns;
    },

    defaultAlias(metric) {
      if (!metric.function || !metric.column) return 'Result name';
      return metricLabel(metric, this.columnDisplayNames);
    },

    emitChange() {
      this.$emit('input', this.toOutput());
    },

    toOutput() {
      const differenceBucketGroup = this.copyDifferenceBucketGroup(
        this.aggregation.differenceBucketGroup,
      );
      return {
        groupBy: this.aggregation.groupBy,
        metrics: this.aggregation.metrics.filter(m => m.column && m.function),
        ...(differenceBucketGroup ? { differenceBucketGroup } : {}),
      };
    },

    isNumericType(dataType) {
      return NUMERIC_SQL_TYPES.includes(dataType);
    },

    friendlyTypeLabel(dataType) {
      return friendlyType(dataType).label;
    },

    typeIcon(dataType) {
      if (this.isNumericType(dataType)) return 'mdi-numeric';
      if (DATE_SQL_TYPES.includes(dataType)) return 'mdi-calendar';
      if (dataType === 'uniqueidentifier') return 'mdi-identifier';
      return 'mdi-format-text';
    },

    /** Technical columns (system keys + guid-typed) are de-prioritized in the Group by
     *  list: sorted under their own subheader, never hidden (they stay selectable). */
    isTechnicalColumn(col) {
      return TECHNICAL_GROUP_BY_COLUMNS.includes(col.name) || col.dataType === 'uniqueidentifier';
    },

    /** Group by autocomplete filter: match the display name OR the raw column name so a
     *  column is findable either way. Subheaders are hidden while a query is active. */
    groupByFilter(item, queryText) {
      if (item && item.header) return false;
      const q = (queryText || '').toString().toLowerCase();
      if (!q) return true;
      return `${item.displayName || ''} ${item.name || ''}`.toLowerCase().includes(q);
    },
  },

  computed: {
    canChangeMetricCount() {
      return !this.aggregation.differenceBucketGroup;
    },

    numericColumns() {
      return this.columns.filter(c => this.isNumericType(c.dataType));
    },

    /** name -> display name map, for the shared metricLabel helper. */
    columnDisplayNames() {
      const map = {};
      this.columns.forEach(c => { if (c && c.name) map[c.name] = c.displayName || c.name; });
      return map;
    },

    /** Group by picker items: friendly columns first, then a "Technical columns" subheader
     *  and the de-prioritized system/guid columns. Each item carries `text` (display name)
     *  for the chip label + search; `name` stays the saved value so the payload is unchanged. */
    groupByItems() {
      const friendly = [];
      const technical = [];
      this.columns.forEach(c => {
        const item = { ...c, text: c.displayName || c.name };
        (this.isTechnicalColumn(c) ? technical : friendly).push(item);
      });
      if (!technical.length) return friendly;
      return [...friendly, { header: 'Technical columns' }, ...technical];
    },
  },

  watch: {
    value: {
      handler(val) {
        if (JSON.stringify(val) !== JSON.stringify(this.toOutput())) {
          this.aggregation = this.initAggregation(val);
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.sql-view-agg-sublabel {
  display: block;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0;
  opacity: 0.75;
}

.sql-view-agg-hint {
  margin: 6px 0 0;
  font-size: 11.5px;
  line-height: 1.4;
  opacity: 0.6;
}
</style>
